const PAGE_JUMP = 8;
const DEFAULT_DEBOUNCE_MS = 250;
const DEFAULT_MIN_CHARS_SERVER = 0;
const DEFAULT_SEARCH_THRESHOLD = 8;

const escapeHtml = (value) => String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const debounce = (fn, wait) => {
    let timer = null;
    const debounced = (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), wait);
    };
    debounced.cancel = () => clearTimeout(timer);
    return debounced;
};

const normalizeItem = (raw, valueKey, labelKey) => {
    if (raw !== null && typeof raw === "object") {
        return {
            value: String(raw[valueKey] ?? raw.value ?? raw.id ?? ""),
            label: String(raw[labelKey] ?? raw.label ?? raw.text ?? raw.name ?? ""),
            description: raw.description ?? raw.subtitle ?? null,
            icon: raw.icon ?? null,
            initials: raw.initials ?? null,
            disabled: !!raw.disabled,
            group: raw.group ?? null,
            raw
        };
    }

    return {
        value: String(raw),
        label: String(raw),
        description: null,
        icon: null,
        initials: null,
        disabled: false,
        group: null,
        raw
    };
};

class RcdDropdown {
    constructor(host, options = {}) {
        this.host = host;
        this.options = {
            placeholder: "Select…",
            searchPlaceholder: "Search…",
            emptyText: "No results found.",
            loadingText: "Loading…",
            valueKey: "value",
            labelKey: "label",
            debounceMs: DEFAULT_DEBOUNCE_MS,
            minCharsServer: DEFAULT_MIN_CHARS_SERVER,
            searchThreshold: DEFAULT_SEARCH_THRESHOLD,
            search: "auto",
            allowClear: false,
            items: null,
            itemsProvider: null,
            endpoint: null,
            searchParam: "query",
            subscribe: null,
            onChange: null,
            renderOption: null,
            iconRenderer: null,
            ...options
        };

        this.multiple = this.options.multiple ?? (host instanceof HTMLSelectElement && host.multiple);

        this.items = [];
        this.filteredItems = [];
        this.query = "";
        this.activeIndex = -1;
        this.open = false;
        this.loading = false;
        this.loadedOnce = false;
        this.value = this.multiple
            ? (Array.isArray(this.options.value) ? [...this.options.value] : [])
            : (this.options.value ?? "");
        this.unsubscribeExternal = null;
        this.requestToken = 0;

        this.runSearch = debounce(this._runSearch.bind(this), this.options.debounceMs);

        this._buildDom();
        this._bindEvents();
        this._resolveInitialItems();
    }

    // ---- DOM construction -------------------------------------------------

    _buildDom() {
        const root = document.createElement("div");
        root.className = "rcd-dropdown";
        root.dataset.open = "false";
        if (this.multiple) {
            root.dataset.multiple = "true";
        }

        const isServerSource = !!(this.options.itemsProvider || this.options.endpoint);
        const searchEnabled = this.options.search === "always"
            || (this.options.search === "auto" && (isServerSource || (this.options.items?.length ?? 0) > this.options.searchThreshold));

        this.isServerSource = isServerSource;
        this.searchEnabled = searchEnabled;

        const control = document.createElement("button");
        control.type = "button";
        control.className = "rcd-dropdown__control rcd-control";
        control.setAttribute("aria-haspopup", "listbox");
        control.setAttribute("aria-expanded", "false");
        if (this.host instanceof HTMLSelectElement && this.host.disabled) {
            control.disabled = true;
        }
        control.innerHTML = this.multiple
            ? `<span class="rcd-dropdown__chips" data-rcd-dropdown-value></span><i data-lucide="chevron-down"></i>`
            : `<span class="rcd-dropdown__value" data-rcd-dropdown-value data-placeholder="true">${escapeHtml(this.options.placeholder)}</span><i data-lucide="chevron-down"></i>`;

        const panel = document.createElement("div");
        panel.className = "rcd-dropdown__panel";
        panel.hidden = false;

        let searchInput = null;
        let clearButton = null;
        let spinnerSlot = null;

        if (searchEnabled) {
            const search = document.createElement("div");
            search.className = "rcd-dropdown__search";
            search.innerHTML = `
                <i data-lucide="search"></i>
                <input type="text" class="rcd-dropdown__search-input" autocomplete="off" spellcheck="false" placeholder="${escapeHtml(this.options.searchPlaceholder)}" role="combobox" aria-expanded="false" aria-autocomplete="list" />
                <span class="rcd-dropdown__spinner-slot"><span class="rcd-spinner" role="status" aria-label="${escapeHtml(this.options.loadingText)}"></span></span>
                <button type="button" class="rcd-dropdown__clear" aria-label="Clear search"><i data-lucide="x"></i></button>
            `;
            panel.append(search);
            searchInput = search.querySelector(".rcd-dropdown__search-input");
            clearButton = search.querySelector(".rcd-dropdown__clear");
            spinnerSlot = search.querySelector(".rcd-dropdown__spinner-slot");
        }

        const list = document.createElement("div");
        list.className = "rcd-dropdown__list";
        list.setAttribute("role", "listbox");
        list.id = `rcd-dropdown-list-${Math.random().toString(36).slice(2, 9)}`;
        panel.append(list);

        root.append(control, panel);
        this.host.insertAdjacentElement("afterend", root);

        if (this.host instanceof HTMLSelectElement) {
            this.host.hidden = true;
            this.host.setAttribute("aria-hidden", "true");
            this.host.tabIndex = -1;
        }

        this.root = root;
        this.control = control;
        this.valueLabel = control.querySelector("[data-rcd-dropdown-value]");
        this.panel = panel;
        this.searchInput = searchInput;
        this.clearButton = clearButton;
        this.spinnerSlot = spinnerSlot;
        this.list = list;

        control.setAttribute("aria-controls", list.id);
        if (searchInput) {
            searchInput.setAttribute("aria-controls", list.id);
        }

        this.options.iconRenderer?.();
    }

    // ---- Data resolution ----------------------------------------------------

    _resolveInitialItems() {
        if (this.host instanceof HTMLSelectElement && !this.options.items && !this.options.itemsProvider && !this.options.endpoint) {
            const items = [...this.host.options]
                .filter((option) => option.value !== "" || option.textContent.trim() !== "")
                .map((option) => ({
                    value: option.value,
                    label: option.textContent.trim(),
                    description: option.dataset.description ?? null,
                    icon: option.dataset.icon ?? null,
                    initials: option.dataset.initials ?? null,
                    disabled: option.disabled,
                    group: option.closest("optgroup")?.label ?? null,
                    raw: option
                }));
            this.setItems(items);
            if (this.multiple) {
                this.value = [...this.host.selectedOptions].map((option) => option.value).filter((value) => value !== "");
                this._renderChips();
            } else {
                const selected = this.host.options[this.host.selectedIndex];
                if (selected && selected.value !== "") {
                    this.setValue(selected.value, { silent: true });
                }
            }
            return;
        }

        if (Array.isArray(this.options.items)) {
            this.setItems(this.options.items.map((item) => normalizeItem(item, this.options.valueKey, this.options.labelKey)));
        }

        if (this.multiple) {
            this._renderChips();
        } else if (this.options.value) {
            this.setValue(this.options.value, { silent: true });
        }

        if (typeof this.options.subscribe === "function") {
            this.unsubscribeExternal = this.options.subscribe((nextItems) => {
                this.setItems((nextItems ?? []).map((item) => normalizeItem(item, this.options.valueKey, this.options.labelKey)));
                this._renderList();
            });
        }

        if (this.isServerSource && !this.searchEnabled) {
            this._runSearch("");
        }
    }

    setItems(items) {
        this.items = items;
        this.filteredItems = items;
        this.loadedOnce = true;
    }

    setValue(value, { silent = false } = {}) {
        this.value = value === null || value === undefined ? "" : String(value);
        const match = this.items.find((item) => item.value === this.value) ?? this.filteredItems.find((item) => item.value === this.value);
        this.valueLabel.textContent = match ? match.label : (this.value ? this.value : this.options.placeholder);
        this.valueLabel.dataset.placeholder = match || this.value ? "false" : "true";

        if (this.host instanceof HTMLSelectElement) {
            this.host.value = this.value;
        }

        if (!silent) {
            this.host.dispatchEvent(new Event("change", { bubbles: true }));
            this.root.dispatchEvent(new CustomEvent("rcd:dropdown:change", {
                bubbles: true,
                detail: { value: this.value, item: match ?? null }
            }));
            this.options.onChange?.(this.value, match ?? null);
        }
    }

    toggleValue(value, { silent = false } = {}) {
        const stringValue = String(value);
        const index = this.value.indexOf(stringValue);
        if (index === -1) {
            this.value = [...this.value, stringValue];
        } else {
            this.value = this.value.filter((existing) => existing !== stringValue);
        }

        if (this.host instanceof HTMLSelectElement) {
            [...this.host.options].forEach((option) => {
                option.selected = this.value.includes(option.value);
            });
        }

        this._renderChips();

        if (!silent) {
            this.host.dispatchEvent(new Event("change", { bubbles: true }));
            const items = this.value
                .map((v) => this.items.find((item) => item.value === v))
                .filter(Boolean);
            this.root.dispatchEvent(new CustomEvent("rcd:dropdown:change", {
                bubbles: true,
                detail: { value: this.value, items }
            }));
            this.options.onChange?.(this.value, items);
        }
    }

    removeValue(value) {
        if (this.value.includes(String(value))) {
            this.toggleValue(value);
            this._renderList();
        }
    }

    _renderChips() {
        if (!this.valueLabel) {
            return;
        }

        const selectedItems = this.value
            .map((v) => this.items.find((item) => item.value === v) ?? this.filteredItems.find((item) => item.value === v))
            .filter(Boolean);

        if (selectedItems.length === 0) {
            this.valueLabel.innerHTML = `<span class="rcd-dropdown__value" data-placeholder="true">${escapeHtml(this.options.placeholder)}</span>`;
            return;
        }

        const maxVisible = this.options.maxVisibleChips ?? 3;
        const visible = selectedItems.slice(0, maxVisible);
        const overflowCount = selectedItems.length - visible.length;

        const chipHtml = visible.map((item) => `
            <span class="rcd-dropdown__chip">
                <span>${escapeHtml(item.label)}</span>
                <button type="button" class="rcd-dropdown__chip-remove" data-remove-value="${escapeHtml(item.value)}" aria-label="Remove ${escapeHtml(item.label)}"><i data-lucide="x"></i></button>
            </span>`).join("");
        const moreHtml = overflowCount > 0 ? `<span class="rcd-dropdown__chip-more">+${overflowCount}</span>` : "";

        this.valueLabel.innerHTML = chipHtml + moreHtml;
        this.options.iconRenderer?.();
    }

    // ---- Search / async loading --------------------------------------------

    async _runSearch(query) {
        if (!this.isServerSource) {
            this._applyClientFilter(query);
            return;
        }

        if (query.length < this.options.minCharsServer && this.options.minCharsServer > 0) {
            this.filteredItems = [];
            this._renderList();
            return;
        }

        const token = ++this.requestToken;
        this.loading = true;
        this._renderList();

        try {
            const results = await this._fetchItems(query);
            if (token !== this.requestToken) {
                return;
            }

            this.items = results;
            this.filteredItems = results;
            this.loadedOnce = true;
        } catch {
            if (token !== this.requestToken) {
                return;
            }

            this.filteredItems = [];
        } finally {
            if (token === this.requestToken) {
                this.loading = false;
                this._renderList();
            }
        }
    }

    async _fetchItems(query) {
        if (typeof this.options.itemsProvider === "function") {
            const result = await this.options.itemsProvider(query);
            return (result ?? []).map((item) => normalizeItem(item, this.options.valueKey, this.options.labelKey));
        }

        if (this.options.endpoint) {
            const url = new URL(this.options.endpoint, window.location.origin);
            if (query) {
                url.searchParams.set(this.options.searchParam, query);
            }

            const response = await fetch(url.toString(), {
                headers: { "X-Requested-With": "XMLHttpRequest" },
                credentials: "same-origin"
            });

            if (!response.ok) {
                throw new Error(`Dropdown fetch failed: ${response.status}`);
            }

            const payload = await response.json();
            const rawItems = Array.isArray(payload) ? payload : (payload.items ?? payload.results ?? []);
            return rawItems.map((item) => normalizeItem(item, this.options.valueKey, this.options.labelKey));
        }

        return [];
    }

    _applyClientFilter(query) {
        const normalized = query.trim().toLowerCase();
        this.filteredItems = normalized === ""
            ? this.items
            : this.items.filter((item) => item.label.toLowerCase().includes(normalized)
                || (item.description ?? "").toLowerCase().includes(normalized));
        this._renderList();
    }

    // ---- Rendering ----------------------------------------------------------

    _renderList() {
        this.activeIndex = this.filteredItems.findIndex((item) => item.value === this.value);
        if (this.activeIndex === -1 && this.filteredItems.length > 0) {
            this.activeIndex = 0;
        }

        if (this.spinnerSlot) {
            this.spinnerSlot.dataset.visible = this.loading && this.loadedOnce ? "true" : "false";
        }

        if (this.loading && !this.loadedOnce) {
            this.list.innerHTML = `
                <div class="rcd-dropdown__skeleton">
                    <div class="rcd-skeleton-group">
                        <span class="rcd-skeleton"></span>
                        <span class="rcd-skeleton rcd-skeleton--short"></span>
                        <span class="rcd-skeleton"></span>
                        <span class="rcd-skeleton rcd-skeleton--short"></span>
                    </div>
                </div>`;
            return;
        }

        if (this.filteredItems.length === 0) {
            this.list.innerHTML = `<div class="rcd-dropdown__empty" role="status">${escapeHtml(this.options.emptyText)}</div>`;
            return;
        }

        let currentGroup;
        const rows = this.filteredItems.map((item, index) => {
            const groupHtml = item.group && item.group !== currentGroup
                ? (currentGroup = item.group, `<div class="rcd-dropdown__group-label">${escapeHtml(item.group)}</div>`)
                : "";

            const isSelected = this.multiple ? this.value.includes(item.value) : item.value === this.value;
            const toggle = this.multiple ? `<span class="rcd-dropdown__option-toggle" aria-hidden="true"></span>` : "";
            const leading = item.icon
                ? `<i data-lucide="${escapeHtml(item.icon)}"></i>`
                : (item.initials
                    ? `<span class="rcd-dropdown__option-icon">${escapeHtml(item.initials)}</span>`
                    : "");
            const description = item.description
                ? `<span class="rcd-dropdown__option-description">${escapeHtml(item.description)}</span>`
                : "";
            const custom = typeof this.options.renderOption === "function" ? this.options.renderOption(item) : null;
            const body = custom ?? `${toggle}${leading}<span class="rcd-dropdown__option-copy"><span class="rcd-dropdown__option-label">${escapeHtml(item.label)}</span>${description}</span><i class="rcd-dropdown__option-check" data-lucide="check"></i>`;

            return `${groupHtml}<button type="button" class="rcd-dropdown__option${index === this.activeIndex ? " is-active" : ""}" role="option" data-index="${index}" aria-selected="${isSelected}" ${item.disabled ? 'aria-disabled="true"' : ""}>${body}</button>`;
        });

        this.list.innerHTML = rows.join("");
        this.options.iconRenderer?.();
        this._syncActiveDescendant();
    }

    _syncActiveDescendant() {
        const active = this.list.children[this.activeIndex];
        if (this.searchInput) {
            if (active?.id) {
                this.searchInput.setAttribute("aria-activedescendant", active.id);
            } else {
                this.searchInput.removeAttribute("aria-activedescendant");
            }
        }
    }

    // ---- Open / close ---------------------------------------------------------

    openPanel() {
        if (this.open || this.control.disabled) {
            return;
        }

        this.open = true;
        this.root.dataset.open = "true";
        this.control.setAttribute("aria-expanded", "true");
        this.searchInput?.setAttribute("aria-expanded", "true");

        if (this.isServerSource && !this.loadedOnce) {
            this._runSearch(this.query);
        } else {
            this._renderList();
        }

        if (this.searchInput) {
            this.searchInput.value = this.query;
            this.searchInput.focus();
            this.searchInput.select();
        }

        this.root.dispatchEvent(new CustomEvent("rcd:dropdown:open", { bubbles: true }));
    }

    closePanel({ restoreFocus = false } = {}) {
        if (!this.open) {
            return;
        }

        this.open = false;
        this.root.dataset.open = "false";
        this.control.setAttribute("aria-expanded", "false");
        this.searchInput?.setAttribute("aria-expanded", "false");
        this.query = "";
        if (this.searchInput) {
            this.searchInput.value = "";
        }
        if (this.clearButton) {
            this.clearButton.dataset.visible = "false";
        }
        this.filteredItems = this.items;

        if (restoreFocus) {
            this.control.focus();
        }

        this.root.dispatchEvent(new CustomEvent("rcd:dropdown:close", { bubbles: true }));
    }

    // ---- Keyboard / pointer handling ------------------------------------------

    _syncFromHost() {
        if (!(this.host instanceof HTMLSelectElement)) {
            return;
        }

        if (this.multiple) {
            const nextValue = [...this.host.selectedOptions].map((option) => option.value);
            this.value = nextValue;
            this._renderChips();
            return;
        }

        if (this.host.value !== this.value) {
            this.setValue(this.host.value, { silent: true });
        }
    }

    _bindEvents() {
        this.host.addEventListener("change", () => this._syncFromHost());

        this.control.addEventListener("click", (event) => {
            const removeButton = event.target.closest(".rcd-dropdown__chip-remove");
            if (removeButton) {
                event.stopPropagation();
                this.removeValue(removeButton.dataset.removeValue);
                return;
            }

            if (this.open) {
                this.closePanel();
            } else {
                this.openPanel();
            }
        });

        this.control.addEventListener("keydown", (event) => this._handleTriggerKeydown(event));

        this.list.addEventListener("click", (event) => {
            const optionEl = event.target.closest(".rcd-dropdown__option");
            if (!optionEl || optionEl.getAttribute("aria-disabled") === "true") {
                return;
            }

            this._choose(Number(optionEl.dataset.index));
        });

        this.list.addEventListener("pointermove", (event) => {
            const optionEl = event.target.closest(".rcd-dropdown__option");
            if (optionEl) {
                this._setActiveIndex(Number(optionEl.dataset.index), { scroll: false });
            }
        });

        if (this.searchInput) {
            this.searchInput.addEventListener("input", () => {
                this.query = this.searchInput.value;
                if (this.clearButton) {
                    this.clearButton.dataset.visible = this.query ? "true" : "false";
                }
                this.runSearch(this.query);
            });

            this.searchInput.addEventListener("keydown", (event) => this._handleSearchKeydown(event));

            this.clearButton?.addEventListener("click", () => {
                this.query = "";
                this.searchInput.value = "";
                this.clearButton.dataset.visible = "false";
                this.searchInput.focus();
                this.runSearch.cancel();
                this._runSearch("");
            });
        }

        document.addEventListener("pointerdown", (event) => {
            if (this.open && !this.root.contains(event.target)) {
                this.closePanel();
            }
        });
    }

    _handleTriggerKeydown(event) {
        if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
            event.preventDefault();
            this.openPanel();
        }
    }

    _handleSearchKeydown(event) {
        const count = this.filteredItems.length;

        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                this._setActiveIndex(Math.min(this.activeIndex + 1, count - 1));
                break;
            case "ArrowUp":
                event.preventDefault();
                this._setActiveIndex(Math.max(this.activeIndex - 1, 0));
                break;
            case "Home":
                event.preventDefault();
                this._setActiveIndex(0);
                break;
            case "End":
                event.preventDefault();
                this._setActiveIndex(count - 1);
                break;
            case "PageDown":
                event.preventDefault();
                this._setActiveIndex(Math.min(this.activeIndex + PAGE_JUMP, count - 1));
                break;
            case "PageUp":
                event.preventDefault();
                this._setActiveIndex(Math.max(this.activeIndex - PAGE_JUMP, 0));
                break;
            case "Enter":
                event.preventDefault();
                if (this.activeIndex >= 0) {
                    this._choose(this.activeIndex);
                }
                break;
            case "Escape":
                event.preventDefault();
                this.closePanel({ restoreFocus: true });
                break;
            case "Tab":
                this.closePanel();
                break;
            default:
                break;
        }
    }

    _setActiveIndex(index, { scroll = true } = {}) {
        if (index < 0 || index >= this.filteredItems.length || this.filteredItems[index]?.disabled) {
            return;
        }

        this.activeIndex = index;
        [...this.list.querySelectorAll(".rcd-dropdown__option")].forEach((el, i) => {
            el.classList.toggle("is-active", i === index);
        });

        if (scroll) {
            this.list.children[index]?.scrollIntoView({ block: "nearest" });
        }

        this._syncActiveDescendant();
    }

    _choose(index) {
        const item = this.filteredItems[index];
        if (!item || item.disabled) {
            return;
        }

        if (this.multiple) {
            this.toggleValue(item.value);
            this._renderList();
            this.searchInput?.focus();
            return;
        }

        this.setValue(item.value);
        this.closePanel({ restoreFocus: true });
    }

    destroy() {
        this.unsubscribeExternal?.();
        this.runSearch.cancel();
        this.root.remove();
        if (this.host instanceof HTMLSelectElement) {
            this.host.hidden = false;
            this.host.removeAttribute("aria-hidden");
            this.host.tabIndex = 0;
        }
    }
}

const instances = new WeakMap();

export const setupRcdDropdownKits = (root = document, iconRenderer = null) => {
    root.querySelectorAll("select.rcd-control[data-rcd-dropdown]:not([data-rcd-dropdown-ready])").forEach((select) => {
        select.dataset.rcdDropdownReady = "true";
        const instance = new RcdDropdown(select, {
            placeholder: select.dataset.rcdDropdownPlaceholder || "Select…",
            searchPlaceholder: select.dataset.rcdDropdownSearchPlaceholder || "Search…",
            emptyText: select.dataset.rcdDropdownEmptyText || "No results found.",
            search: select.dataset.rcdDropdownSearch || "auto",
            endpoint: select.dataset.rcdDropdownEndpoint || null,
            searchParam: select.dataset.rcdDropdownSearchParam || "query",
            debounceMs: select.dataset.rcdDropdownDebounce ? Number(select.dataset.rcdDropdownDebounce) : DEFAULT_DEBOUNCE_MS,
            iconRenderer
        });
        instances.set(select, instance);
    });
};

export const RcdDropdownKit = {
    create(host, options = {}) {
        const instance = new RcdDropdown(host, options);
        instances.set(host, instance);
        return instance;
    },
    get(host) {
        return instances.get(host) ?? null;
    },
    destroy(host) {
        instances.get(host)?.destroy();
        instances.delete(host);
    }
};

if (typeof window !== "undefined") {
    window.RcdDropdownKit = RcdDropdownKit;
}
